import React, { Component } from 'react'
import cx from 'classnames'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import Blasts from './Blasts'
import MessagesContainer from './MessagesContainer'
import MessagesEditor from './MessagesEditor'
import MessagesFilter from './MessagesFilter'
import MessagesHeader from './Header'
import { fetchMessageSettings } from '../../state/actions/message-settings'
import ConversationsContainer from './ConversationsContainer'
import LoadingIcon from './LoadingIcon'
import GoBackButton from './GoBackButton'
import TextUnknownDialog from './TextUnknownDialog'
import DiscardMessageDialog from './DiscardMessageDialog'
import * as ConvoMsgActions from '../../state/actions/conversations-messages'
import {
  chooseCurrentConversation,
  isCurrConversationEmail,
  resetCurrentConversation,
} from '../../state/actions/current'
import { autoChooseActiveConvo } from './utils'
import css from './styles/index.css'

const { REACT_APP_IS_TOPS } = process.env

class Messages extends Component {
  static propTypes = {
    activeLocation: PropTypes.shape({
      active: PropTypes.bool.isRequired,
      additional_recipients: PropTypes.arrayOf(PropTypes.object),
      city: PropTypes.string,
      country_code: PropTypes.string,
      created_at: PropTypes.string,
      doctor_id: PropTypes.string,
      earliest_same_day_reminder_hour: PropTypes.number,
      facebook_url: PropTypes.string,
      forwarding_email: PropTypes.string,
      google_url: PropTypes.string,
      id: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      name: PropTypes.string,
      office_email: PropTypes.string,
      office_phone: PropTypes.string,
      phone_numbers: PropTypes.arrayOf(PropTypes.object),
      practice_id: PropTypes.number,
      practice_name: PropTypes.string,
      state: PropTypes.string,
      street_address: PropTypes.string,
      timezone: PropTypes.string,
      updated_at: PropTypes.string,
      website: PropTypes.string,
      yelp_url: PropTypes.string,
      zip: PropTypes.string,
    }),
    getTemplates: PropTypes.func.isRequired,
    addMediaToMessage: PropTypes.func.isRequired,
    attachedMedia: PropTypes.string,
    convoId: PropTypes.number,
    convoMessages: PropTypes.array.isRequired,
    getConversations: PropTypes.func.isRequired,
    getMessagesForConversation: PropTypes.func.isRequired,
    hasDSO: PropTypes.bool.isRequired,
    archiveConvo: PropTypes.func.isRequired,
    hasRemindersTier2: PropTypes.bool.isRequired,
    hasTopsRemindersPlus: PropTypes.bool.isRequired,
    history: PropTypes.shape({
      goBack: PropTypes.func.isRequired,
    }).isRequired,
    isEmail: PropTypes.bool.isRequired,
    isBlastMsg: PropTypes.bool.isRequired,
    isSending: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isCurrConversationEmail: PropTypes.func.isRequired,
    locations: PropTypes.arrayOf(PropTypes.object),
    userLocations: PropTypes.arrayOf(PropTypes.object),
    resetCurrentConversation: PropTypes.func.isRequired,
    search: PropTypes.string.isRequired,
    searchConversations: PropTypes.func.isRequired,
    selectConversation: PropTypes.func.isRequired,
    storeConversationDetails: PropTypes.func.isRequired,
    templateSettings: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    updateConvoMsgTypeFilter: PropTypes.func.isRequired,
  }

  state = {
    activeDateFilterString: '',
    convoTabIndex: 0,
    dateChanged: false,
    pendingConvoId: null,
    endDate: null,
    isEditorFocused: false,
    radioDisabled: false,
    searchAllLocations: false,
    selectedMessageId: false, // for emails only, identify which message to focus on.
    selectedLocationId: this.props.activeLocation
      ? this.props.activeLocation.id
      : '',
    selectedRadioBtn: 'all',
    startDate: null,
    textUnknownDialogOpen: false,
    unknownPhoneNumber: '',
    fetchController: null,
  }

  async componentDidMount() {
    const { activeLocation, getTemplates } = this.props
    this.fetchNewConversationList()
    await getTemplates(activeLocation && activeLocation.id)
  }

  componentWillReceiveProps({
    isEmail,
    convoMessages,
    activeLocation: nextActiveLocation,
    globalMsgTypeFilter,
  }) {
    const { activeLocation, hasDSO } = this.props
    /*
      if a message comes in that's a different type than the current filter,
      we're preventing the adding of the convo to the convo list until they click
      the notification. globalMsgTypeFilter will always be synced with selectedRadioBtn
      until they click the notification. so once they're out of sync, we change the radio btn
      and reset all the filters.
    */
    if (globalMsgTypeFilter !== this.state.selectedRadioBtn) {
      this.setState(
        {
          startDate: null,
          endDate: null,
          activeDateFilterString: '',
          dateChanged: false,
          selectedLocationId: '',
          selectedRadioBtn: globalMsgTypeFilter,
          radioDisabled: true,
        },
        this.fetchNewConversationList,
      )
    }
    if (isEmail && convoMessages.length) {
      this.setState({
        selectedMessageId: convoMessages[convoMessages.length - 1].id,
      })
    }

    if (hasDSO && nextActiveLocation.id !== activeLocation.id) {
      this.setState(
        {
          selectedLocationId: nextActiveLocation.id,
        },
        this.fetchNewConversationList,
      )
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.search !== prevProps.search) {
      this.fetchNewConversationList()
    }
  }

  componentWillUnmount() {
    this.props.updateConvoMsgTypeFilter('all')
  }

  toggleEditorFocus = () => {
    this.setState({ isEditorFocused: !this.state.isEditorFocused })
  }

  toggleTextUnknownDialog = openDialog => {
    this.setState({ textUnknownDialogOpen: openDialog })
  }

  handleChangePhone = e => {
    this.setState({ unknownPhoneNumber: e.target.value })
  }

  setPendingConvoId = pendingConvoId => this.setState({ pendingConvoId })

  handleConvoClick = selectedId => {
    const { hasBodyText } = this.props
    if (hasBodyText) {
      this.setPendingConvoId(selectedId)
    } else {
      this.changeConversationAndFetchMessages(selectedId)
    }
  }

  changeConversationAndFetchMessages = selectedId => {
    const {
      selectConversation,
      isCurrConversationEmail,
      getMessagesForConversation,
    } = this.props
    this.setPendingConvoId(null)
    selectConversation(selectedId)
    isCurrConversationEmail()
    if (selectedId === -1) return null
    return getMessagesForConversation(selectedId)
  }

  handleClearFilter = type => {
    switch (type) {
      case 'selectedLocationId':
        this.setState({ selectedLocationId: '' }, this.fetchNewConversationList)
        break
      case 'selectedTimeRange':
        this.setState(
          {
            startDate: null,
            endDate: null,
            activeDateFilterString: '',
            dateChanged: false,
          },
          this.fetchNewConversationList,
        )
        break
      case 'selectedSearchTerm':
        this.props.searchConversations('')
        break
      case 'selectedEmailOrText':
        this.setState({ selectedRadioBtn: 'all' }, () => {
          this.props.updateConvoMsgTypeFilter('all')
          this.fetchNewConversationList()
        })
        break
      default:
        break
    }
  }

  handleMessageFocus = val => {
    // pass in `false` to "unselect" something
    this.setState({ selectedMessageId: val })
  }

  handlePendingMediaPreview = media => {
    const { addMediaToMessage, convoId } = this.props
    addMediaToMessage(media, convoId)
  }

  handleSlide = (value, fetchConvos = true) => {
    if (!fetchConvos) return this.setState({ convoTabIndex: value })
    return this.setState(
      { convoTabIndex: value },
      this.fetchNewConversationList,
    )
  }

  handleTextUnknownSubmit = () => {
    const { storeConversationDetails, selectConversation } = this.props
    const unknownNumberConvo = {
      id: -1,
      active: false,
      seen: true,
      messages_type: 'text',
      subject: null,
      patients: [],
      patient_phone: this.state.unknownPhoneNumber,
    }
    storeConversationDetails(unknownNumberConvo)
    selectConversation(-1)
    this.handleSlide(1, false)
    this.toggleTextUnknownDialog(false)
  }

  handleArchiveButtonClick = (active, convoId) => {
    const {
      archiveConvo,
      activeConvos,
      selectConversation,
      isCurrConversationEmail,
      getMessagesForConversation,
      resetCurrentConversation,
    } = this.props

    const { convoTabIndex } = this.state

    archiveConvo(active, convoId).then(() => {
      if (convoTabIndex === 0) {
        const updatedIdsList = activeConvos.idsList.delete(convoId)
        const nextActiveConvoId = updatedIdsList.first()

        if (updatedIdsList.isEmpty()) {
          resetCurrentConversation()
          return
        }

        selectConversation(nextActiveConvoId)
        isCurrConversationEmail()
        if (nextActiveConvoId !== -1) {
          getMessagesForConversation(nextActiveConvoId)
        }
      }
    })
  }

  handleRadioBtnSelect = (_e, value) => {
    this.setState({ selectedRadioBtn: value, radioDisabled: true }, () => {
      this.props.updateConvoMsgTypeFilter(value)
      this.fetchNewConversationList()
    })
  }

  handleAllLocationsCheckbox = e => {
    this.setState(
      { searchAllLocations: e.target.checked },
      this.fetchNewConversationList,
    )
  }

  handleSearchChange = e => {
    this.props.searchConversations(e.target.value)
  }

  handleDateTimeSearch = () =>
    this.setState(
      {
        dateChanged: false,
        activeDateFilterString: this.setTimeRangeLabel(),
      },
      this.fetchNewConversationList,
    )

  newConversationQueryParams = () => {
    const {
      selectedRadioBtn,
      selectedLocationId,
      searchAllLocations,
      startDate,
      endDate,
    } = this.state

    const { search, hasDSO, activeLocation } = this.props

    const queryParams = {}

    if (search) queryParams.search = search

    if (selectedLocationId && !hasDSO) {
      queryParams.conversation_location_id = selectedLocationId
    } else if (hasDSO) {
      queryParams.conversation_location_id = activeLocation.id
    }

    if (searchAllLocations && hasDSO) {
      queryParams.all_locations = searchAllLocations
    }

    if (startDate) queryParams.start_date_time = startDate.utc().format()

    if (endDate) queryParams.end_date_time = endDate.utc().format()

    if (selectedRadioBtn === 'text' || selectedRadioBtn === 'email') {
      queryParams.messages_type = selectedRadioBtn
    }

    return queryParams
  }

  fetchNewConversationList = () => {
    const {
      getConversations,
      getMessagesForConversation,
      isBlastMsg,
      convoId,
      activeConvos,
      allConvos,
    } = this.props
    const { convoTabIndex } = this.state

    if (this.state.fetchController) this.state.fetchController.abort()

    const fetchController = new AbortController()

    getConversations(
      {
        activeStatus: convoTabIndex ? 'all' : 'active',
        chooseActiveConvo: autoChooseActiveConvo(convoId, isBlastMsg),
      },
      this.newConversationQueryParams(),
      1,
      fetchController,
      ).then(() => {
        if (!isBlastMsg && convoId && convoId !== -1) {
          getMessagesForConversation(convoId)
        }

        this.setState({ radioDisabled: false })
      }).catch(err => {
        // this catches the fetchController.abort()
        // console.error(`getConversations error: ${err.message}`)
      })

    this.setState({ fetchController })
  }

  selectConversationLocation = e =>
    this.setState(
      { selectedLocationId: e.target.value },
      this.fetchNewConversationList,
    )

  selectConversationDateTime = (date, type) =>
    this.setState({
      dateChanged: true,
      [type]: date ? date.local(true) : date,
    })

  setTimeRangeLabel = () => {
    const { startDate, endDate } = this.state
    if (startDate && endDate) {
      return `From ${moment(startDate).format('lll')} to ${moment(
        endDate,
      ).format('lll')}`
    } else if (startDate && !endDate) {
      return `Since ${moment(startDate).format('lll')}`
    } else if (endDate && !startDate) {
      return `Before ${moment(endDate).format('lll')}`
    }

    return ''
  }

  generatePageHeight = () => {
    const { hasDSO, userLocations, isBlastMsg } = this.props
    const { selectedRadioBtn, activeDateFilterString: hasTimeFilter } =
      this.state

    let height = '79vh'
    const isTops = REACT_APP_IS_TOPS
    const showConversations = !isTops || (isTops && !isBlastMsg)
    const hasTopMenu = hasDSO && userLocations && userLocations.length > 1
    const hasMessageTypeFilter = selectedRadioBtn !== 'all'
    const hasActiveFilters = hasMessageTypeFilter || !!hasTimeFilter
    if (showConversations) {
      if (hasTopMenu && hasActiveFilters) {
        height = '64vh'
      } else if (hasTopMenu || hasActiveFilters) {
        height = '70vh'
      }
    }
    return height
  }

  render() {
    const {
      attachedMedia,
      byId,
      convoId,
      convoMessages,
      hasDSO,
      hasRemindersTier2,
      hasTopsRemindersPlus,
      history,
      isEmail,
      isSending,
      isLoading,
      isBlastMsg,
      locations,
      messageSettingsById,
      practiceName,
      search,
      templateSettings,
      userLocations,
    } = this.props

    const {
      activeDateFilterString,
      convoTabIndex,
      dateChanged,
      endDate,
      isEditorFocused,
      pendingConvoId,
      radioDisabled,
      selectedMessageId,
      selectedLocationId,
      selectedRadioBtn,
      searchAllLocations,
      startDate,
      textUnknownDialogOpen,
      unknownPhoneNumber,
    } = this.state

    const isTops = REACT_APP_IS_TOPS
    const showBackButton = isTops && isBlastMsg
    const showConversations = !isTops || (isTops && !isBlastMsg)
    const height = this.generatePageHeight()

    return (
      <div className={css.wrapper}>
        {!isBlastMsg && (
          <MessagesFilter
            activeDateFilterString={activeDateFilterString}
            fetchNewConversationList={this.fetchNewConversationList}
            hasDSO={hasDSO}
            handleAllLocationsCheckbox={this.handleAllLocationsCheckbox}
            handleClearFilter={this.handleClearFilter}
            handleDateTimeChange={this.selectConversationDateTime}
            handleDateTimeSearch={this.handleDateTimeSearch}
            handleRadioBtnSelect={this.handleRadioBtnSelect}
            handleSearchChange={this.handleSearchChange}
            locations={locations}
            userLocations={userLocations}
            radioDisabled={radioDisabled}
            searchValue={search}
            selectConversationLocation={this.selectConversationLocation}
            selectedLocationId={selectedLocationId}
            selectedRadioBtn={selectedRadioBtn}
            searchAllLocations={searchAllLocations}
            startDate={startDate}
            endDate={endDate}
            dateChanged={dateChanged}
          />
        )}
        <Blasts />
        <div
          className={showConversations ? css.page : css.narrowpage}
          style={{ height }}
        >
          {showBackButton && <GoBackButton onClick={history.goBack} />}
          <div className={css.main}>
            <MessagesHeader
              convoTabIndex={convoTabIndex}
              handleArchiveButtonClick={this.handleArchiveButtonClick}
            />
            {isLoading && <LoadingIcon isEmail={isEmail} isTops={isTops} />}
            <MessagesContainer
              messages={convoMessages}
              isEmail={isEmail}
              isBlastMsg={isBlastMsg}
              isSending={isSending}
              isEditorFocused={isEditorFocused}
              conversationId={convoId}
              selectedMessageId={selectedMessageId}
              handleMessageFocus={this.handleMessageFocus}
              attachedMedia={attachedMedia}
              previewPendingMedia={this.handlePendingMediaPreview}
              isPatientsPage={false}
              hasRemindersTier2={hasRemindersTier2}
              hasTopsRemindersPlus={hasTopsRemindersPlus}
              conversationById={byId}
              practiceName={practiceName}
            />
            {(convoId || isBlastMsg) && (
              <MessagesEditor
                isFocused={isEditorFocused}
                templateSettings={templateSettings}
                messageSettingsById={messageSettingsById}
                toggleFocus={this.toggleEditorFocus}
                previewPendingMedia={this.handlePendingMediaPreview}
                hasRemindersTier2={hasRemindersTier2}
                unknownNumber={unknownPhoneNumber}
              />
            )}
          </div>
          {showConversations && (
            <ConversationsContainer
              handleConvoClick={this.handleConvoClick}
              height={height}
              convoTabIndex={convoTabIndex}
              emailOnly={selectedRadioBtn === 'email'}
              handleSlide={this.handleSlide}
              handleTextUnknownClick={() => this.toggleTextUnknownDialog(true)}
              queryParams={this.newConversationQueryParams()}
            />
          )}
        </div>
        <TextUnknownDialog
          closeDialog={() => this.toggleTextUnknownDialog(false)}
          onChangePhone={this.handleChangePhone}
          onSubmit={this.handleTextUnknownSubmit}
          open={textUnknownDialogOpen}
          phone={unknownPhoneNumber}
        />
        <DiscardMessageDialog
          pendingConvoId={pendingConvoId}
          onClose={() => this.setPendingConvoId(null)}
          onSubmit={this.changeConversationAndFetchMessages}
        />
      </div>
    )
  }
}

const mapStateToProps = ({
  messages: { byId: messagesById, dataByConversation },
  conversations: {
    byId,
    activeConvos,
    allConvos,
    locations,
    search,
    messageType,
  },
  current: {
    conversation: { id: convoId, isEmail, isBlastMsg, hasBodyText },
  },
  account: {
    auth: { hasDSO, hasRemindersTier2, hasTopsRemindersPlus },
    user: { practiceName, activeLocation, locations: userLocations },
  },
  messageSettings: { byId: messageSettingsById, idsByType },
}) => {
  let convoMessages = []
  let sending = false
  let loading = false
  let media = null
  let templateSettings = []

  if (dataByConversation.hasOwnProperty(convoId)) {
    const { isSending, isLoading, messageIds, attachedMedia } =
      dataByConversation[convoId]
    media = attachedMedia
    convoMessages = messageIds.map(msgId => messagesById[msgId])
    sending = !!isSending
    loading = !!isLoading
  } else {
    sending = false
    loading = false
  }

  if (idsByType.hasOwnProperty('template')) {
    templateSettings = idsByType.template.map(
      settingId => messageSettingsById[settingId],
    )
  }

  return {
    activeLocation,
    attachedMedia: media,
    byId,
    activeConvos,
    allConvos,
    convoId,
    convoMessages,
    isEmail,
    isBlastMsg,
    isSending: sending,
    isLoading: loading,
    hasDSO,
    hasRemindersTier2,
    hasTopsRemindersPlus,
    locations,
    messagesById,
    messageSettingsById,
    practiceName,
    userLocations,
    search,
    templateSettings,
    hasBodyText,
    globalMsgTypeFilter: messageType,
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      ...ConvoMsgActions,
      selectConversation: chooseCurrentConversation,
      isCurrConversationEmail,
      resetCurrentConversation,
      searchConversations: searchTerm =>
        ConvoMsgActions.updateConversationSearch(searchTerm),
      updateConvoMsgTypeFilter: msgType =>
        ConvoMsgActions.updateConversationMessageType(msgType),
      getTemplates: loc => fetchMessageSettings(loc, true),
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(Messages)



// WEBPACK FOOTER //
// ./src/components/Messages/index.js